/**
 * PurchaseRegisterManualEntryController — modale réutilisable de saisie
 * manuelle scellée du registre des achats (EPIC-018 / 18.5, §8.bis.D).
 *
 * Branchée sur `<main>` des deux pages :
 *  - `register.html` (Consultation, §8.bis.E)
 *  - `register-processing.html` (Tenue, §8.bis.C)
 *
 * Le HTML de la modale vit dans `<template id="manual-entry-template">`
 * dupliqué dans chaque page (verbatim maquette `registre-achats-tenue.html`
 * section `#manual-entry`). À `connect()`, le template est cloné et
 * inséré comme premier enfant de l'élément racine du controller.
 *
 * Deux modes :
 *  - **Création** — déclenchée par `open()` (bouton « + Écriture manuelle »).
 *    Réinitialise tous les champs, `correctionOfEntryId = ""`,
 *    `entryDate = today`.
 *  - **Correction** — déclenchée par `openCorrection({ params: { … } })`.
 *    Pré-remplit montants négatifs, `correctionOfEntryId`, supplier_name,
 *    description = « Annulation écriture #N ». Cache le champ PJ
 *    (la correction ne porte pas de PJ propre).
 *
 * Soumission : `multipart/form-data` via `api.upload` si PJ jointe,
 * sinon `application/json` via `api.post`. Codes d'erreur API mappés
 * vers la zone d'erreur inline (423 verbatim « L'exercice [année] est
 * clôturé. »).
 *
 * Émet un événement DOM `purchase-register:entry-created` après succès
 * pour permettre aux controllers parents (consultation, processing) de
 * rafraîchir leur liste.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class PurchaseRegisterManualEntryController extends BaseController {
  static targets = [
    "modal", "form", "errorBox", "submitButton", "title", "intro", "hint",
    "correctionOfEntryId",
    "entryDate", "supplierName", "supplierSiret", "pieceReference", "description",
    "amountExclVat", "vatRate", "vatAmount", "amountInclVat",
    "paymentMethod", "attachment", "attachmentFieldset",
  ];

  connect() {
    if (!this.hasModalTarget) {
      const fragment = this.cloneTemplate("manual-entry-template");
      if (fragment && fragment.firstElementChild) {
        this.element.insertBefore(fragment, this.element.firstChild);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  open(event) {
    if (event) event.preventDefault();
    if (!this.hasModalTarget) return;

    this._resetForm();
    if (this.hasCorrectionOfEntryIdTarget) this.correctionOfEntryIdTarget.value = "";
    if (this.hasTitleTarget) this.titleTarget.textContent = "Écriture manuelle";
    if (this.hasAttachmentFieldsetTarget) this.attachmentFieldsetTarget.hidden = false;

    if (this.hasEntryDateTarget) {
      this.entryDateTarget.value = this._todayIso();
    }
    this._clearError();
    this.modalTarget.hidden = false;
    this._focusFirstField();
  }

  openCorrection(event) {
    if (event) event.preventDefault();
    if (!this.hasModalTarget) return;

    const params = event && event.params ? event.params : {};
    const entryId = params.entryId || "";
    const entryNumber = params.entryNumber || "";
    const supplierName = params.supplierName || "";
    const amountExclVat = params.amountExclVat || "0";
    const vatAmount = params.vatAmount || "0";
    const amountInclVat = params.amountInclVat || "0";
    const paymentMethod = params.paymentMethod || "";
    const pieceReference = params.pieceReference || "";
    const entryDate = params.entryDate || this._todayIso();

    this._resetForm();
    if (this.hasCorrectionOfEntryIdTarget) this.correctionOfEntryIdTarget.value = entryId;
    if (this.hasTitleTarget) this.titleTarget.textContent = "Écriture corrective";
    if (this.hasAttachmentFieldsetTarget) this.attachmentFieldsetTarget.hidden = true;

    if (this.hasEntryDateTarget) this.entryDateTarget.value = entryDate;
    if (this.hasSupplierNameTarget) this.supplierNameTarget.value = supplierName;
    if (this.hasPieceReferenceTarget) this.pieceReferenceTarget.value = pieceReference;
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.value = entryNumber
        ? `Annulation écriture #${entryNumber}`
        : "Annulation écriture";
    }
    if (this.hasAmountExclVatTarget) this.amountExclVatTarget.value = this._negate(amountExclVat);
    if (this.hasVatAmountTarget) this.vatAmountTarget.value = this._negate(vatAmount);
    if (this.hasAmountInclVatTarget) this.amountInclVatTarget.value = this._negate(amountInclVat);
    if (this.hasPaymentMethodTarget && paymentMethod) {
      this.paymentMethodTarget.value = paymentMethod;
    }

    this._clearError();
    this.modalTarget.hidden = false;
    this._focusFirstField();
  }

  close(event) {
    if (event) event.preventDefault();
    if (!this.hasModalTarget) return;
    this.modalTarget.hidden = true;
    this._clearError();
  }

  // ---------------------------------------------------------------------------
  // Auto-calc montants HT / TVA / TTC
  // ---------------------------------------------------------------------------

  recalc(event) {
    const trigger = event && event.target ? event.target.dataset.amount : null;
    const ht = this._readAmount(this.hasAmountExclVatTarget ? this.amountExclVatTarget.value : "");
    const tva = this._readAmount(this.hasVatAmountTarget ? this.vatAmountTarget.value : "");
    const ttc = this._readAmount(this.hasAmountInclVatTarget ? this.amountInclVatTarget.value : "");
    const rate = this.hasVatRateTarget ? parseFloat(this.vatRateTarget.value || "0") : 0;

    if (trigger === "ht") {
      if (!Number.isNaN(rate)) {
        const newTva = ht * rate;
        if (this.hasVatAmountTarget) this.vatAmountTarget.value = this._formatAmount(newTva);
        if (this.hasAmountInclVatTarget) this.amountInclVatTarget.value = this._formatAmount(ht + newTva);
      }
    } else if (trigger === "tva") {
      if (!Number.isNaN(ht)) {
        if (this.hasAmountInclVatTarget) this.amountInclVatTarget.value = this._formatAmount(ht + tva);
      }
    } else if (trigger === "ttc") {
      if (!Number.isNaN(rate) && rate > 0) {
        const newHt = ttc / (1 + rate);
        const newTva = ttc - newHt;
        if (this.hasAmountExclVatTarget) this.amountExclVatTarget.value = this._formatAmount(newHt);
        if (this.hasVatAmountTarget) this.vatAmountTarget.value = this._formatAmount(newTva);
      } else {
        if (this.hasAmountExclVatTarget) this.amountExclVatTarget.value = this._formatAmount(ttc);
        if (this.hasVatAmountTarget) this.vatAmountTarget.value = "0,00";
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault();
    if (!this.hasFormTarget) return;

    this._clearError();

    const payload = this._collectPayload();
    if (!this._validatePayload(payload)) return;

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;

    try {
      const file = this._collectAttachment();
      const response = file
        ? await api.upload("/purchases/register/entries", file, payload, "attachment")
        : await api.post("/purchases/register/entries", payload);

      this.showSuccess(
        payload.correctionOfEntryId
          ? "Écriture corrective ajoutée au registre."
          : "Écriture manuelle ajoutée au registre.",
      );
      this.modalTarget.hidden = true;
      window.dispatchEvent(
        new CustomEvent("purchase-register:entry-created", { detail: { entry: response } }),
      );
    } catch (error) {
      this._renderApiError(error);
    } finally {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  _collectPayload() {
    const payload = {};
    if (this.hasCorrectionOfEntryIdTarget && this.correctionOfEntryIdTarget.value) {
      payload.correctionOfEntryId = this.correctionOfEntryIdTarget.value;
    }
    if (this.hasEntryDateTarget) payload.entryDate = this.entryDateTarget.value;
    if (this.hasSupplierNameTarget) payload.supplierName = this.supplierNameTarget.value.trim();
    if (this.hasSupplierSiretTarget) {
      const siret = this.supplierSiretTarget.value.replace(/\s+/g, "");
      if (siret !== "") payload.supplierSiret = siret;
    }
    if (this.hasPieceReferenceTarget) payload.pieceReference = this.pieceReferenceTarget.value.trim();
    if (this.hasDescriptionTarget) payload.description = this.descriptionTarget.value.trim();
    if (this.hasAmountExclVatTarget) payload.amountExclVat = this._normalizeAmount(this.amountExclVatTarget.value);
    if (this.hasVatAmountTarget) payload.vatAmount = this._normalizeAmount(this.vatAmountTarget.value);
    if (this.hasAmountInclVatTarget) payload.amountInclVat = this._normalizeAmount(this.amountInclVatTarget.value);
    if (this.hasPaymentMethodTarget) payload.paymentMethod = this.paymentMethodTarget.value;
    return payload;
  }

  _collectAttachment() {
    if (!this.hasAttachmentTarget) return null;
    if (this.hasAttachmentFieldsetTarget && this.attachmentFieldsetTarget.hidden) return null;
    const files = this.attachmentTarget.files;
    if (!files || files.length === 0) return null;
    return files[0];
  }

  _validatePayload(payload) {
    const requiredFields = [
      ["entryDate", "Date d'achat obligatoire."],
      ["supplierName", "Fournisseur obligatoire."],
      ["pieceReference", "Référence de pièce obligatoire."],
      ["description", "Libellé obligatoire."],
      ["amountExclVat", "Montant HT obligatoire."],
      ["vatAmount", "Montant TVA obligatoire."],
      ["amountInclVat", "Montant TTC obligatoire."],
      ["paymentMethod", "Mode de règlement obligatoire."],
    ];
    for (const [field, message] of requiredFields) {
      if (!payload[field] || payload[field] === "") {
        this._showError(message);
        return false;
      }
    }
    const ht = parseFloat(payload.amountExclVat);
    const tva = parseFloat(payload.vatAmount);
    const ttc = parseFloat(payload.amountInclVat);
    if (Number.isNaN(ht) || Number.isNaN(tva) || Number.isNaN(ttc)) {
      this._showError("Montants invalides.");
      return false;
    }
    if (Math.abs(ht + tva - ttc) > 0.01) {
      this._showError("Incohérence des montants : HT + TVA doit être égal au TTC.");
      return false;
    }
    return true;
  }

  _renderApiError(error) {
    const message = error && error.body && error.body.error && error.body.error.message
      ? error.body.error.message
      : "Une erreur est survenue lors de l'enregistrement.";
    this._showError(message);
  }

  _showError(message) {
    if (!this.hasErrorBoxTarget) {
      this.showError(message);
      return;
    }
    this.errorBoxTarget.textContent = message;
    this.errorBoxTarget.hidden = false;
  }

  _clearError() {
    if (!this.hasErrorBoxTarget) return;
    this.errorBoxTarget.textContent = "";
    this.errorBoxTarget.hidden = true;
  }

  _resetForm() {
    if (this.hasFormTarget) {
      this.formTarget.reset();
    }
  }

  _focusFirstField() {
    if (this.hasEntryDateTarget) this.entryDateTarget.focus();
  }

  _todayIso() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  _readAmount(raw) {
    if (typeof raw !== "string" || raw === "") return 0;
    const normalized = raw.replace(/\s+/g, "").replace(",", ".");
    const value = parseFloat(normalized);
    return Number.isNaN(value) ? 0 : value;
  }

  _normalizeAmount(raw) {
    if (typeof raw !== "string") return "";
    return raw.replace(/\s+/g, "").replace(",", ".");
  }

  _formatAmount(value) {
    if (Number.isNaN(value)) return "0,00";
    return value.toFixed(2).replace(".", ",");
  }

  _negate(amount) {
    const value = parseFloat(String(amount).replace(",", "."));
    if (Number.isNaN(value) || value === 0) return "0,00";
    return this._formatAmount(-value);
  }
}
